import { useCallback, useEffect, useRef } from 'react';
import { isNil } from 'lodash';

/**
 * Custom hook for polling an API endpoint with configurable options.
 *
 * Features:
 * - Configurable polling interval with optional exponential backoff
 * - Optional timeout and max attempts limits
 * - Automatic cleanup on unmount
 * - Request overlap prevention
 * - Manual start/stop control
 *
 * @param {Object} options - Polling configuration
 * @param {Function} options.pollFn - Async function to call on each poll (receives attempt count)
 * @param {Function} options.onPoll - Callback after each successful poll with response, returns true to continue or false to stop
 * @param {number} [options.interval=3000] - Initial polling interval in milliseconds
 * @param {number} [options.maxTimeout] - Maximum total time in milliseconds (optional)
 * @param {number} [options.maxAttempts] - Maximum number of attempts (optional)
 * @param {Function} [options.onTimeout] - Callback when timeout is reached
 * @param {Function} [options.onMaxAttempts] - Callback when max attempts reached
 * @param {Function} [options.onError] - Callback for errors during polling
 * @param {boolean} [options.useSetInterval=false] - Use setInterval instead of recursive setTimeout
 * @param {boolean} [options.useBackoff=false] - Use exponential backoff to gradually increase polling interval
 * @param {number} [options.backoffMultiplier=2] - Multiplier for exponential backoff (default: 2x)
 * @param {number} [options.maxInterval] - Maximum interval cap for backoff (optional, defaults to 30000ms)
 *
 * @returns {Object} Polling control object
 * @returns {Function} returns.start - Start polling
 * @returns {Function} returns.stop - Stop polling
 * @returns {boolean} returns.isActive - Whether polling is currently active
 * @returns {number} returns.attemptCount - Current attempt count
 * @returns {number} returns.elapsedTime - Elapsed time in milliseconds since start
 *
 * @example
 * // Simple interval-based polling (like DeviceImportDialog)
 * const polling = usePollingApi({
 *   pollFn: async () => dataService.get('/api/import-status', { id: importId }),
 *   onPoll: (response) => {
 *     setStatus(response.status);
 *     setMessage(response.message);
 *     return response.status === 'processing'; // Continue if still processing
 *   },
 *   interval: 500,
 *   useSetInterval: true,
 *   onError: (error) => {
 *     setError('Failed to check status');
 *   }
 * });
 *
 * @example
 * // Timeout-based polling with limits (like VcenterVerify)
 * const polling = usePollingApi({
 *   pollFn: async (attemptCount) => {
 *     return dataService.post('/api/test-connectivity', { testId, attemptCount });
 *   },
 *   onPoll: (response) => {
 *     updateStatus(response);
 *     return response.trying === true; // Continue if still trying
 *   },
 *   interval: 1000,
 *   maxTimeout: 60000,
 *   maxAttempts: 60,
 *   onTimeout: () => setStatus('Timeout'),
 *   onMaxAttempts: () => setStatus('Max attempts reached'),
 *   useSetInterval: false // Use recursive setTimeout
 * });
 *
 * @example
 * // Exponential backoff polling (reduces server load over time)
 * const polling = usePollingApi({
 *   pollFn: async () => dataService.get('/api/long-running-task', { taskId }),
 *   onPoll: (response) => response.status === 'processing',
 *   interval: 1000,           // Start with 1s
 *   useBackoff: true,         // Enable exponential backoff
 *   backoffMultiplier: 1.5,   // 1s, 1.5s, 2.25s, 3.38s, 5.06s, 7.59s...
 *   maxInterval: 10000,       // Cap at 10s
 *   useSetInterval: false
 * });
 */
const usePollingApi = ({
    pollFn,
    onPoll,
    interval = 3000,
    maxTimeout,
    maxAttempts,
    onTimeout,
    onMaxAttempts,
    onError,
    useSetInterval = false,
    useBackoff = false,
    backoffMultiplier = 2,
    maxInterval = 30000,
}) => {
    const pollingRef = useRef({
        timerId: null,
        startTime: null,
        isActive: false,
        isRequestInProgress: false,
        attemptCount: 0,
        currentInterval: interval,
    });

    // Calculate elapsed time since polling started
    const getElapsedTime = useCallback(() => {
        if (isNil(pollingRef.current.startTime)) {
            return 0;
        }
        return Date.now() - pollingRef.current.startTime;
    }, []);

    // Check if limits have been exceeded
    const checkLimits = useCallback(() => {
        const { attemptCount } = pollingRef.current;
        const elapsedTime = getElapsedTime();

        if (!isNil(maxTimeout) && elapsedTime >= maxTimeout) {
            return { exceeded: true, reason: 'timeout' };
        }

        if (!isNil(maxAttempts) && attemptCount >= maxAttempts) {
            return { exceeded: true, reason: 'maxAttempts' };
        }

        return { exceeded: false };
    }, [maxTimeout, maxAttempts, getElapsedTime]);

    // Calculate next interval for exponential backoff
    const getNextInterval = useCallback(() => {
        if (!useBackoff) {
            return interval;
        }

        const nextInterval = pollingRef.current.currentInterval * backoffMultiplier;
        return Math.min(nextInterval, maxInterval);
    }, [useBackoff, interval, backoffMultiplier, maxInterval]);

    // Stop polling and cleanup
    const stop = useCallback(() => {
        if (pollingRef.current.timerId) {
            if (useSetInterval) {
                clearInterval(pollingRef.current.timerId);
            } else {
                clearTimeout(pollingRef.current.timerId);
            }
            pollingRef.current.timerId = null;
        }
        pollingRef.current.isActive = false;
        pollingRef.current.isRequestInProgress = false;
        pollingRef.current.attemptCount = 0;
        pollingRef.current.startTime = null;
        pollingRef.current.currentInterval = interval;
    }, [useSetInterval, interval]);

    // Execute a single poll iteration
    const executePoll = useCallback(async () => {
        // Prevent overlapping requests (for setTimeout mode)
        if (!useSetInterval && pollingRef.current.isRequestInProgress) {
            return;
        }

        // Check if still active (user might have stopped manually)
        if (!pollingRef.current.isActive) {
            return;
        }

        // Increment attempt count
        pollingRef.current.attemptCount += 1;

        // Check limits before making request
        const limitCheck = checkLimits();
        if (limitCheck.exceeded) {
            stop();
            if (limitCheck.reason === 'timeout' && onTimeout) {
                onTimeout();
            } else if (limitCheck.reason === 'maxAttempts' && onMaxAttempts) {
                onMaxAttempts();
            }
            return;
        }

        // Mark request as in progress (for setTimeout mode)
        if (!useSetInterval) {
            pollingRef.current.isRequestInProgress = true;
        }

        try {
            const response = await pollFn(pollingRef.current.attemptCount);

            // Reset in-progress flag
            if (!useSetInterval) {
                pollingRef.current.isRequestInProgress = false;
            }

            // Check if still active (might have been stopped during async call)
            if (!pollingRef.current.isActive) {
                return;
            }

            // Determine if polling should continue based on response
            const shouldContinuePolling = onPoll(response);

            if (!shouldContinuePolling) {
                stop();
            } else if (!useSetInterval && pollingRef.current.isActive) {
                // Calculate next interval (with backoff if enabled)
                const nextInterval = getNextInterval();
                pollingRef.current.currentInterval = nextInterval;

                // Schedule next poll (setTimeout mode only, setInterval handles this automatically)
                pollingRef.current.timerId = setTimeout(executePoll, nextInterval);
            }
        } catch (error) {
            // Reset in-progress flag
            if (!useSetInterval) {
                pollingRef.current.isRequestInProgress = false;
            }

            stop();

            if (onError) {
                onError(error);
            }
        }
    }, [pollFn, onPoll, useSetInterval, checkLimits, stop, onTimeout, onMaxAttempts, onError, getNextInterval]);

    // Start polling
    const start = useCallback(() => {
        // Stop any existing polling
        stop();

        // Initialize state
        pollingRef.current.isActive = true;
        pollingRef.current.isRequestInProgress = false;
        pollingRef.current.startTime = Date.now();
        pollingRef.current.attemptCount = 0;
        pollingRef.current.currentInterval = interval;

        if (useSetInterval) {
            // Note: setInterval doesn't support dynamic intervals, so backoff is only for setTimeout mode
            if (useBackoff) {
                // eslint-disable-next-line no-console
                console.warn(
                    'usePollingApi: Exponential backoff is not supported with useSetInterval=true. Use useSetInterval=false for backoff.'
                );
            }
            // Use setInterval for simple periodic polling
            pollingRef.current.timerId = setInterval(executePoll, interval);
            // Execute first poll immediately
            executePoll();
        } else {
            // Use recursive setTimeout for more control (and backoff support)
            executePoll();
        }
    }, [stop, useSetInterval, interval, useBackoff, executePoll]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stop();
        };
    }, [stop]);

    return {
        start,
        stop,
        get isActive() {
            return pollingRef.current.isActive;
        },
        get attemptCount() {
            return pollingRef.current.attemptCount;
        },
        get elapsedTime() {
            return getElapsedTime();
        },
    };
};

export default usePollingApi;
